import { useState } from "react";
import { Button, StatusBadge, Skeleton, Textarea, useToast } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { Modal } from "@/components/ui/Modal";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { ApiError, extractErrorMessage } from "@/api/client";
import { useDateFormatter } from "@/utils/datetime";
import {
  useInterviewWordingEditor,
  usePublishInterviewWording,
  useRevertInterviewWording,
} from "@/hooks/interviewWording/useInterviewWording";
import type { InterviewWordingEditor, InterviewWordingItem } from "@/types/interviewWording";
import { SectionPanel } from "./interviewWording/SectionPanel";
import { VersionList } from "./interviewWording/VersionList";
import { publishRefusalLines } from "./interviewWording/publishRefusal";
import { REVERT_CONFIRM_SENTENCE } from "./interviewWording/labels";

const DESCRIPTION =
  "Reword the buyer qualification interview. Words only — question ids, choice keys, order, " +
  "branches and the required set never change here. Each edit saves as a draft; the interview " +
  "keeps its current wording until you publish.";

/**
 * Admin "Interview Wording" — reword the buyer qualification interview without a
 * code change.
 *
 * 🔴 WORDS ONLY. Every control on this page edits text. Ids, option keys, order,
 * branches, the required set, Retired and length caps are shown for orientation
 * and offered as no field; the server refuses anything else regardless.
 *
 * Saving is not publishing: a draft changes nothing the interview reads. Publish
 * (every pending draft, as one version) and Revert to default (one item, as a
 * version of its own) are the only actions that change what is live, and both
 * also freshen the served interview registry.
 */
export function InterviewWordingPage() {
  const query = useInterviewWordingEditor();
  const editor = query.data;

  if (editor === undefined) {
    return (
      <div className="flex flex-col gap-space-4">
        <PageHeader eyebrow="Admin" title="Interview Wording" description={DESCRIPTION} />
        {query.isError ? (
          <div role="alert" className="flex flex-wrap items-center gap-space-3 text-small text-status-error">
            <span>Couldn&apos;t load the interview wording: {extractErrorMessage(query.error)}</span>
            <Button size="sm" variant="secondary" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <Skeleton className="h-24 w-full" />
        )}
      </div>
    );
  }

  return <Editor editor={editor} />;
}

function Editor({ editor }: { editor: InterviewWordingEditor }) {
  const toast = useToast();
  const { date } = useDateFormatter();
  const publish = usePublishInterviewWording();
  const revert = useRevertInterviewWording();

  const [publishOpen, setPublishOpen] = useState(false);
  const [note, setNote] = useState("");
  const [refusal, setRefusal] = useState<string[] | null>(null);
  const [revertTarget, setRevertTarget] = useState<InterviewWordingItem | null>(null);

  const versionLine =
    editor.currentVersion === 0
      ? "In force: Coded defaults"
      : `In force: Version ${editor.currentVersion}` +
        (editor.currentVersionPublishedAtUtc ? ` · published ${date(editor.currentVersionPublishedAtUtc)}` : "");
  const pending = editor.pendingDraftCount;

  const handlePublish = () => {
    const trimmed = note.trim();
    void publish
      .mutateAsync({ note: trimmed === "" ? null : trimmed })
      .then((result) => {
        setPublishOpen(false);
        setNote("");
        setRefusal(null);
        toast.success(`Published version ${result.currentVersion}. The interview reads it from now on.`);
      })
      .catch((e) => {
        setPublishOpen(false);
        // Only a 400 is a refusal ("nothing was published"); any other failure
        // may have landed after the version committed, so say what is known.
        if (e instanceof ApiError && e.status === 400) {
          setRefusal(publishRefusalLines(e));
        } else {
          const reason = extractErrorMessage(e).trim().replace(/[.\s]+$/, "");
          toast.error(`Publish failed: ${reason}. Reload to see which version is in force.`);
        }
      });
  };

  const handleRevert = () => {
    const target = revertTarget;
    setRevertTarget(null);
    if (!target) return;
    void revert
      .mutateAsync(target.blockKey)
      .then((result) =>
        toast.success(`Reverted to the default wording — published as version ${result.currentVersion}.`),
      )
      .catch((e) => toast.error(extractErrorMessage(e)));
  };

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Admin"
        title="Interview Wording"
        description={DESCRIPTION}
        actions={
          <div className="flex flex-wrap items-center gap-space-2">
            <span className="text-small text-gray-mid">{versionLine}</span>
            {pending > 0 ? (
              <StatusBadge tone="warn">
                {pending === 1 ? "1 draft waiting to publish" : `${pending} drafts waiting to publish`}
              </StatusBadge>
            ) : (
              <StatusBadge tone="ok">No drafts waiting</StatusBadge>
            )}
            <Button onClick={() => setPublishOpen(true)} disabled={pending === 0 || publish.isPending}>
              Publish
            </Button>
          </div>
        }
      />

      {refusal ? (
        <div
          role="alert"
          className="flex flex-col gap-space-2 rounded-md border border-status-error bg-status-error/12 p-space-4"
        >
          <p className="m-0 text-small font-medium text-status-error">
            Publish refused — nothing was published. Fix these and publish again:
          </p>
          <ul className="m-0 flex list-disc flex-col gap-space-1 pl-space-4 text-small">
            {refusal.map((line, i) => (
              <li key={`${i}:${line}`} className="whitespace-pre-line">
                {line}
              </li>
            ))}
          </ul>
          <div>
            <Button size="sm" variant="text" onClick={() => setRefusal(null)}>
              Dismiss
            </Button>
          </div>
        </div>
      ) : null}

      {editor.sections.map((section) => (
        <SectionPanel
          key={section.sectionId}
          section={section}
          revertingKey={revert.isPending ? (revert.variables ?? null) : null}
          onRevert={setRevertTarget}
        />
      ))}

      <VersionList versions={editor.versions} />

      <Modal
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        title="Publish the drafts?"
        size="sm"
        dismissOnBackdropClick={false}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPublishOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePublish} loading={publish.isPending}>
              Publish now
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-space-3">
          <p className="m-0 text-small">
            {pending === 1 ? "The 1 pending draft goes" : `All ${pending} pending drafts go`} live together as
            one new version, and the interview reads the new wording from then on.
          </p>
          <Textarea
            value={note}
            rows={3}
            aria-label="Publish note (optional)"
            placeholder="Note (optional) — what changed and why"
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </Modal>

      <ConfirmModal
        open={revertTarget != null}
        title="Revert to the default wording?"
        message={
          <div className="flex flex-col gap-space-2">
            <p className="m-0">{REVERT_CONFIRM_SENTENCE}</p>
            {revertTarget ? (
              <p className="m-0 text-caption text-gray-mid">Default: {revertTarget.defaultText}</p>
            ) : null}
          </div>
        }
        confirmLabel="Publish the default"
        onConfirm={handleRevert}
        onCancel={() => setRevertTarget(null)}
      />
    </div>
  );
}
